import { useState, useEffect } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { formatDistanceToNow } from "date-fns";
import { 
  BookOpen, 
  GraduationCap, 
  Target, 
  Video, 
  Trophy,
  MessageSquare,
  Activity,
  Loader2
} from "lucide-react";

interface ActivityItem {
  id: string;
  type: 'skill_added' | 'skill_learning' | 'challenge_joined' | 'session' | 'showcase' | 'achievement';
  title: string;
  description: string;
  timestamp: string;
  icon: React.ReactNode;
}

const RecentActivity = ({ userId }: { userId?: string }) => {
  const { user } = useAuth();
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const targetUserId = userId || user?.id;

  useEffect(() => {
    if (!targetUserId) return;
    fetchActivities();
  }, [targetUserId]);

  const fetchActivities = async () => {
    if (!targetUserId) return;
    
    try {
      const allActivities: ActivityItem[] = [];

      // Fetch user skills (teaching)
      const { data: teachingSkills } = await supabase
        .from('user_skills')
        .select('id, created_at, skill:skills(name, emoji)')
        .eq('user_id', targetUserId)
        .order('created_at', { ascending: false })
        .limit(5);

      if (teachingSkills) {
        teachingSkills.forEach((item: any) => {
          allActivities.push({
            id: `teach-${item.id}`,
            type: 'skill_added',
            title: 'Added teaching skill',
            description: `${item.skill?.emoji || '📚'} ${item.skill?.name || 'Unknown skill'}`,
            timestamp: item.created_at,
            icon: <GraduationCap className="h-4 w-4 text-primary" />
          });
        });
      }

      // Fetch user learning goals
      const { data: learningSkills } = await supabase
        .from('user_learning')
        .select('id, created_at, skill:skills(name, emoji)')
        .eq('user_id', targetUserId)
        .order('created_at', { ascending: false })
        .limit(5);

      if (learningSkills) {
        learningSkills.forEach((item: any) => {
          allActivities.push({
            id: `learn-${item.id}`,
            type: 'skill_learning',
            title: `Added ${item.skill?.name || 'a skill'} to learn`,
            description: `${item.skill?.emoji || '📖'} ${item.skill?.name || 'Unknown skill'}`,
            timestamp: item.created_at,
            icon: <BookOpen className="h-4 w-4 text-blue-500" />
          });
        });
      }

      // Fetch challenge participations
      const { data: challenges } = await supabase
        .from('user_seasonal_challenges')
        .select('id, joined_at, challenge:seasonal_challenges(title)')
        .eq('user_id', targetUserId)
        .order('joined_at', { ascending: false })
        .limit(5);

      if (challenges) {
        challenges.forEach((item: any) => {
          allActivities.push({
            id: `challenge-${item.id}`,
            type: 'challenge_joined',
            title: 'Joined challenge',
            description: item.challenge?.title || 'Unknown challenge',
            timestamp: item.joined_at,
            icon: <Target className="h-4 w-4 text-orange-500" />
          });
        });
      }

      // Fetch showcase posts
      const { data: showcases } = await supabase
        .from('showcase_posts')
        .select('id, created_at, title')
        .eq('user_id', targetUserId)
        .order('created_at', { ascending: false })
        .limit(5);

      if (showcases) {
        showcases.forEach((item: any) => {
          allActivities.push({
            id: `showcase-${item.id}`,
            type: 'showcase',
            title: 'Posted showcase',
            description: item.title || 'Skill demonstration',
            timestamp: item.created_at,
            icon: <Video className="h-4 w-4 text-purple-500" />
          });
        });
      }

      // Fetch achievements
      const { data: achievements } = await supabase
        .from('user_achievements')
        .select('id, earned_at, achievement:achievements(title, icon)')
        .eq('user_id', targetUserId)
        .order('earned_at', { ascending: false })
        .limit(5);

      if (achievements) {
        achievements.forEach((item: any) => {
          allActivities.push({
            id: `achievement-${item.id}`,
            type: 'achievement',
            title: 'Earned achievement',
            description: `${item.achievement?.icon || '🏆'} ${item.achievement?.title || 'Unknown'}`,
            timestamp: item.earned_at,
            icon: <Trophy className="h-4 w-4 text-yellow-500" />
          });
        });
      }

      // Sort all activities by timestamp
      allActivities.sort((a, b) => 
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );

      setActivities(allActivities.slice(0, 15));
    } catch (error) {
      console.error('Error fetching activities:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className="text-center py-8">
        <Activity className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          No activity yet. Start exploring to see your journey!
        </p>
      </div>
    );
  }

  return (
    <ScrollArea className="h-[200px] pr-4">
      <div className="space-y-3">
        {activities.map((activity) => (
          <div 
            key={activity.id} 
            className="flex items-start gap-3 p-2 rounded-lg hover:bg-secondary/50 transition-colors"
          >
            <div className="p-2 bg-secondary rounded-full shrink-0">
              {activity.icon}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{activity.title}</p>
              <p className="text-xs text-muted-foreground truncate">{activity.description}</p>
              <p className="text-xs text-muted-foreground/70 mt-0.5">
                {formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}
              </p>
            </div>
          </div>
        ))}
      </div>
    </ScrollArea>
  );
};

export default RecentActivity;
